import React, { useState, useMemo } from 'react';
import { useSiteData, DataContextProps } from '@context';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { getSafeName } from '@utilities';
import { Button } from '@base-components';

/**
 * Resource List.
 */
const ResourceList: React.FC<{
  listType: string;
  isNode: boolean;
}> = ({ listType, isNode }) => {
  const { siteData }: DataContextProps = useSiteData();
  const [filterListItems, setFilterListItems] = useState<string[]>([]);
  const [selectCancerType, setSelectCancerType] = useState('');
  const allEmpowerMePages = siteData?.allEmpowerMePages ?? null;

  /**
   * Get Cancer Types.
   */
  const getCancerTypes = (): string[] => {
    const listItems: string[] = [];
    allEmpowerMePages?.nodes?.forEach((story) => {
      const { content_type: contentType, cancer_type: cancerType } =
        story.frontmatter;
      if (
        contentType === 'patient-stories' &&
        cancerType &&
        !listItems.includes(cancerType) &&
        story.frontmatter.language === siteData?.translations?.language
      ) {
        listItems.push(cancerType);
      }
    });
    return listItems;
  };

  /**
   * Check if Patient Story page.
   */
  useMemo(() => {
    if (!isNode && listType === 'patient-stories') {
      const cancerTypeList = getCancerTypes();
      setFilterListItems(cancerTypeList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!siteData) return null;
  const { currentLocale } = siteData;

  /**
   * Render.
   */
  if (allEmpowerMePages) {
    const sortedStories = allEmpowerMePages?.nodes?.sort((a, b) => {
      if (a.frontmatter.title < b.frontmatter.title) {
        return -1;
      }
      if (a.frontmatter.title > b.frontmatter.title) {
        return 1;
      }
      return 0;
    });

    const allListItems = ['all-cancer-types', ...filterListItems];

    return (
      <div className="resource-list-wrapper">
        {/* Filter */}
        {filterListItems && filterListItems.length > 0 && (
          <div className="cancer-type-filters">
            {allListItems.map((item) => {
              const translationKey = item.toLowerCase().replace(/\s|-/g, '_');
              const translatedItem = siteData?.translations?.[translationKey];
              const allSelected =
                selectCancerType === '' && item === 'all-cancer-types';
              const itemSelected = selectCancerType === item;

              return (
                <Button
                  key={item}
                  dataButtonVariation={
                    allSelected || itemSelected ? '' : 'outline'
                  }
                  onButtonClick={() => setSelectCancerType(item)}
                >
                  {(allSelected || itemSelected) && (
                    <i className="icon-check" />
                  )}
                  {translatedItem}
                </Button>
              );
            })}
          </div>
        )}

        {/* Filter Items */}
        <div
          className={
            listType === 'patient-stories'
              ? 'grid-3-columns grid'
              : 'grid-2-columns grid'
          }
        >
          {sortedStories?.map((story, index: number) => {
            const {
              title,
              teaser,
              page_image: pageImage,
              content_type: contentType,
              node_type: nodeType,
              path,
              cancer_type: cancerType,
              thumbnail_background_color: thumbnailBackgroundColor,
            } = story.frontmatter;
            const image = getImage(pageImage);
            const safeCancerType = cancerType ? getSafeName(cancerType) : '';

            /**
             * Node listing.
             */
            if (
              isNode &&
              listType === nodeType &&
              contentType !== 'patient-stories' &&
              currentLocale === story.frontmatter.language
            ) {
              return (
                <div className="resource-listing" key={index}>
                  <h3>{title}</h3>
                  <p>
                    {teaser}
                    <Button
                      dataButtonType="control"
                      dataButtonVariation="outline"
                      onButtonClick={() => {
                        window.open(path, '_self')?.focus();
                      }}
                    >
                      <i className="icon-arrow-right-medium" />
                    </Button>
                  </p>
                </div>
              );
            }
            /**
             * Patient Stories (Filtered card layout).
             */
            if (
              !isNode &&
              contentType === 'patient-stories' &&
              (safeCancerType.includes(selectCancerType) ||
                selectCancerType === 'all-cancer-types') &&
              currentLocale === story.frontmatter.language
            ) {
              const linkMessage = story.frontmatter?.link_message
                ? story.frontmatter?.link_message
                : `Watch ${title.split(' ')[0]}'s story`;

              return (
                <div className={`resource-card`} key={index}>
                  {image && (
                    <div
                      className={`img-container ${thumbnailBackgroundColor ?? ''}`}
                    >
                      <GatsbyImage image={image} alt={title} />
                    </div>
                  )}
                  <div className="text-container center-vertically">
                    <h3>{title}</h3>
                    <p>{teaser}</p>
                  </div>
                  <div className="link-container">
                    <Button
                      dataButtonVariation="outline"
                      dataSize="sm"
                      onButtonClick={() => {
                        window.open(path, '_self')?.focus();
                      }}
                    >
                      {linkMessage}
                    </Button>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    );
  }
  return null;
};

export default ResourceList;
